// SPDX-License-Identifier: AGPL-3.0-or-later

import {Button} from '@app/features/ui/button/Button';
import {AudioLevelMeter} from '@app/features/user/components/modals/tabs/components/AudioLevelMeter';
import styles from '@app/features/user/components/modals/tabs/components/MicTestSection.module.css';
import type {MicTestSettings} from '@app/features/user/components/modals/tabs/hooks/useMicTest';
import {useMicTest} from '@app/features/user/components/modals/tabs/hooks/useMicTest';
import VoiceSettings from '@app/features/voice/state/VoiceSettings';
import {Trans} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';

interface MicTestSectionProps {
	settings: MicTestSettings;
}

export const MicTestSection: React.FC<MicTestSectionProps> = observer(({settings}) => {
	const {isTesting, level, start, stop} = useMicTest(settings);
	const autoSensitivity = VoiceSettings.vadAutoSensitivity;
	const threshold = VoiceSettings.vadThreshold;
	return (
		<div data-flx="user.mic-test-section.div">
			<div className={styles.label} data-flx="user.mic-test-section.label">
				<Trans>Mic test</Trans>
			</div>
			<div className={styles.content} data-flx="user.mic-test-section.content">
				<AudioLevelMeter
					level={isTesting ? level : 0}
					thresholdPercent={threshold}
					showThreshold={isTesting && !autoSensitivity}
					data-flx="user.mic-test-section.audio-level-meter"
				/>
				<div className={styles.controls} data-flx="user.mic-test-section.controls">
					<Button
						variant="primary"
						className={styles.actionButton}
						onClick={isTesting ? stop : start}
						data-flx="user.mic-test-section.action-button.stop"
					>
						{isTesting ? <Trans>Stop test</Trans> : <Trans>Test microphone</Trans>}
					</Button>
					{isTesting && (
						<span className={styles.listening} data-flx="user.mic-test-section.listening">
							<Trans>Listening...</Trans>
						</span>
					)}
				</div>
			</div>
		</div>
	);
});
